import React, { useEffect, useState } from 'react';
import { Check, ArrowRight, Sparkles } from 'lucide-react';
import { useTheme } from './ThemeContext';

const WelcomePage = ({ companyName, onContinue }) => {
  const { colors } = useTheme();
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    // Animate in after a brief delay
    const timer = setTimeout(() => setShowContent(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      minHeight: '100dvh',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #0a0a0a 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
      position: 'relative',
      overflowX: 'hidden',
      overflowY: 'auto'
    }}>
      {/* Background decorations */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '10%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(240, 90, 40, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '10%',
        right: '10%',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(78,204,163,0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none'
      }} />

      <div style={{
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        opacity: showContent ? 1 : 0,
        transform: showContent ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.5s ease-out'
      }}>
        {/* Success checkmark */}
        <div style={{
          width: '80px',
          height: '80px',
          background: 'linear-gradient(135deg, #4ecca3 0%, #2ecc71 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 2rem',
          boxShadow: '0 10px 40px rgba(78,204,163,0.3)'
        }}>
          <Check size={40} color="#fff" strokeWidth={3} />
        </div>

        {/* Welcome text */}
        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: 'bold',
          color: '#e0e0e0',
          marginBottom: '0.5rem'
        }}>
          Welcome to Work Van!
        </h1>

        {companyName && (
          <p style={{
            fontSize: '1.25rem',
            color: colors.accent,
            marginBottom: '1.5rem',
            fontWeight: '600'
          }}>
            {companyName}
          </p>
        )}

        {/* Trial info card */}
        <div style={{
          background: 'rgba(78,204,163,0.1)',
          border: '1px solid rgba(78,204,163,0.3)',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            marginBottom: '0.75rem'
          }}>
            <Sparkles size={20} color="#4ecca3" />
            <span style={{
              color: '#4ecca3',
              fontWeight: 'bold',
              fontSize: '1.1rem'
            }}>
              14-Day Free Trial Active
            </span>
          </div>
          <p style={{
            color: '#888',
            fontSize: '0.95rem',
            margin: 0,
            lineHeight: 1.5
          }}>
            You have full access to all features. Your card won't be charged until the trial ends.
          </p>
        </div>

        {/* What's next */}
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          textAlign: 'left'
        }}>
          <h3 style={{
            color: '#e0e0e0',
            fontSize: '1rem',
            marginBottom: '1rem',
            fontWeight: 'bold'
          }}>
            Quick Start:
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {[
              'Add your first customer',
              'Create a quote or schedule a job',
              'Customize your company settings'
            ].map((item, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                color: '#aaa',
                fontSize: '0.95rem'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: '#2a2a2a',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.8rem',
                  color: colors.accent,
                  fontWeight: 'bold',
                  flexShrink: 0
                }}>
                  {idx + 1}
                </div>
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* Continue button */}
        <button
          onClick={onContinue}
          onTouchEnd={(e) => {
            e.preventDefault();
            onContinue();
          }}
          style={{
            width: '100%',
            padding: '1rem 2rem',
            background: colors.accentGradient,
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            fontSize: '1.1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            boxShadow: '0 4px 20px rgba(240, 90, 40, 0.3)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            WebkitTapHighlightColor: 'transparent'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 30px rgba(240, 90, 40, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(240, 90, 40, 0.3)';
          }}
        >
          Let's Go! <ArrowRight size={20} />
        </button>

        <p style={{
          color: '#666',
          fontSize: '0.85rem',
          marginTop: '1.5rem'
        }}>
          Need help? Check out Settings → Support anytime.
        </p>
      </div>
    </div>
  );
};

export default WelcomePage;
